import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './LeaderBoard.module.css';
import { useNavigate } from 'react-router-dom';
import Paddle from '../Paddle/Paddle';
import CupBoard from '../Shared/CupBoard/CupBoard';
import { formatNumber, safeDivide } from '../../utils/formatters';

const playerLabel = (player, index) =>
  `${index + 1}. ${player.nickname || player.username}`;

const TABS = [
  {
    id: 'games',
    label: 'Games',
    getItems: (lb) =>
      (lb.games_completed || []).slice(0, 15).map((p, i) => ({
        label: playerLabel(p, i),
        value: String(p.total_games_completed),
      })),
  },
  {
    id: 'wins',
    label: 'Wins',
    getItems: (lb) =>
      (lb.wins || []).slice(0, 15).map((p, i) => ({
        label: playerLabel(p, i),
        value: String(p.wins),
      })),
  },
  {
    id: 'win_pct',
    label: 'Win %',
    getItems: (lb) =>
      (lb.win_pct || []).slice(0, 15).map((p, i) => ({
        label: playerLabel(p, i),
        value: formatNumber(p.winning_percentage * 100) + '%',
      })),
  },
  {
    id: 'plus_minus',
    label: '+/-',
    getItems: (lb) =>
      (lb.plus_minus || [])
        .map((p) => ({
          ...p,
          plusMinus:
            safeDivide(p.total_score_for, p.total_games_completed) -
            safeDivide(p.total_score_against, p.total_games_completed),
        }))
        .filter((p) => p.total_games_completed >= 3)
        .sort((a, b) => b.plusMinus - a.plusMinus)
        .slice(0, 15)
        .map((p, i) => ({
          label: playerLabel(p, i),
          value:
            p.plusMinus >= 0
              ? `+${formatNumber(p.plusMinus)}`
              : formatNumber(p.plusMinus),
        })),
  },
  {
    id: 'net_cups',
    label: 'Net Cups',
    getItems: (lb) =>
      (lb.cups_per_game || []).slice(0, 15).map((p, i) => ({
        label: playerLabel(p, i),
        value: formatNumber(p.total_cups_per_game - p.own_cups_per_game),
      })),
  },
  {
    id: 'saves',
    label: 'Saves',
    getItems: (lb) =>
      (lb.saves_per_game || []).slice(0, 15).map((p, i) => ({
        label: playerLabel(p, i),
        value: formatNumber(p.saves_per_game),
      })),
  },
  {
    id: 'laps_given',
    label: 'Laps Given',
    getItems: (lb) =>
      (lb.laps_given || []).slice(0, 15).map((p, i) => ({
        label: playerLabel(p, i),
        value: String(p.laps_given),
      })),
  },
  {
    id: 'laps_received',
    label: 'Laps Rcvd',
    getItems: (lb) =>
      (lb.laps_received || []).slice(0, 15).map((p, i) => ({
        label: playerLabel(p, i),
        value: String(p.laps_received),
      })),
  },
  {
    id: 'per_cup',
    label: 'Per-Cup',
    getItems: null,
  },
];

// Cup positions in board order
const AT_POSITIONS = [
  { key: 'bk_attack',  label: 'Back' },
  { key: 'ct_attack',  label: 'Center' },
  { key: 'lf_attack',  label: 'Left' },
  { key: 'rt_attack',  label: 'Right' },
  { key: 'fr_attack',  label: 'Front' },
];
const DF_POSITIONS = [
  { key: 'fr_defense', label: 'Front' },
  { key: 'rt_defense', label: 'Right' },
  { key: 'lf_defense', label: 'Left' },
  { key: 'ct_defense', label: 'Center' },
  { key: 'bk_defense', label: 'Back' },
];

const leaderLabel = (d) =>
  `${d?.nickname || d?.username || '—'} ${formatNumber(d?.value)}`;

const PerCupLeaders = ({ leaderboard }) => {
  return (
    <div className={styles.perCupWrapper}>

      {/* Compact heat-map board */}
      <div className={styles.perCupBoardCard}>
        <p className={styles.perCupBoardLabel}>Cup leaders — heat map by hit rate</p>
        <p className={styles.perCupSideLabel}>Attacking →</p>
        <CupBoard
          variant="compact"
          showHeat
          attackData={{
            bk: parseFloat(leaderboard.bk_attack?.value) || 0,
            ct: parseFloat(leaderboard.ct_attack?.value) || 0,
            lf: parseFloat(leaderboard.lf_attack?.value) || 0,
            rt: parseFloat(leaderboard.rt_attack?.value) || 0,
            fr: parseFloat(leaderboard.fr_attack?.value) || 0,
          }}
          defenseData={{
            fr: parseFloat(leaderboard.fr_defense?.value) || 0,
            rt: parseFloat(leaderboard.rt_defense?.value) || 0,
            lf: parseFloat(leaderboard.lf_defense?.value) || 0,
            ct: parseFloat(leaderboard.ct_defense?.value) || 0,
            bk: parseFloat(leaderboard.bk_defense?.value) || 0,
          }}
          attackLabels={{
            bk: leaderLabel(leaderboard.bk_attack),
            ct: leaderLabel(leaderboard.ct_attack),
            lf: leaderLabel(leaderboard.lf_attack),
            rt: leaderLabel(leaderboard.rt_attack),
            fr: leaderLabel(leaderboard.fr_attack),
          }}
          defenseLabels={{
            fr: leaderLabel(leaderboard.fr_defense),
            rt: leaderLabel(leaderboard.rt_defense),
            lf: leaderLabel(leaderboard.lf_defense),
            ct: leaderLabel(leaderboard.ct_defense),
            bk: leaderLabel(leaderboard.bk_defense),
          }}
        />
        <p className={styles.perCupSideLabel}>← Defending</p>
        <div className={styles.perCupLegend}>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.legendDotRed}`} />
            <span>Attacking</span>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.legendDotBlue}`} />
            <span>Defending</span>
          </div>
          <span className={styles.legendHint}>Hover cup for leader</span>
        </div>
      </div>

      {/* Leader tables */}
      <div className={styles.perCupLeaderGrid}>
        <div className={styles.statsSection}>
          <p className={`${styles.sectionTitle} ${styles.sectionTitleRed}`}>Attack Cups Leaders</p>
          {AT_POSITIONS.map(({ key, label }) => {
            const d = leaderboard[key];
            return (
              <div key={key} className={styles.statRow}>
                <span className={styles.posLabel}>{label}</span>
                <span className={styles.leaderName}>{d?.nickname || d?.username || '—'}</span>
                <span className={styles.statValue}>{d ? formatNumber(d.value) : '—'}</span>
              </div>
            );
          })}
        </div>

        <div className={styles.statsSection}>
          <p className={`${styles.sectionTitle} ${styles.sectionTitleBlue}`}>Own Cups Leaders</p>
          {DF_POSITIONS.map(({ key, label }) => {
            const d = leaderboard[key];
            return (
              <div key={key} className={styles.statRow}>
                <span className={styles.posLabel}>{label}</span>
                <span className={styles.leaderName}>{d?.nickname || d?.username || '—'}</span>
                <span className={styles.statValue}>{d ? formatNumber(d.value) : '—'}</span>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState(null);
  const [activeTab, setActiveTab] = useState('games');
  const [paddlePage, setPaddlePage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/c/getleaderboard/`)
      .then((response) => {
        setLeaderboard(response.data.leaderboard);
      })
      .catch((error) => {
        console.error('Error fetching leaderboard data:', error);
      });
  }, []);

  useEffect(() => {
    setPaddlePage(0);
  }, [activeTab]);

  if (!leaderboard) {
    return <div className={styles.loading}>Loading...</div>;
  }

  const handleBack = () => navigate(-1);

  const activeConfig = TABS.find((t) => t.id === activeTab);
  const allItems =
    activeConfig && activeConfig.getItems
      ? activeConfig.getItems(leaderboard)
      : null;

  const totalPages = allItems ? Math.min(3, Math.ceil(allItems.length / 5)) : 0;

  const pageSlice = (pageIndex) =>
    allItems ? allItems.slice(pageIndex * 5, pageIndex * 5 + 5) : null;

  const currentItems = pageSlice(paddlePage);
  const ghost1Items = totalPages > 1 ? pageSlice((paddlePage + 1) % totalPages) : null;
  const ghost2Items = totalPages > 2 ? pageSlice((paddlePage + 2) % totalPages) : null;

  const handleCycle = () => setPaddlePage((p) => (p + 1) % totalPages);

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={handleBack}>
        ← Back
      </button>
      <h1 className={styles.title}>Leaderboard</h1>
      <p className={styles.subtitle}>Minimum 5 games</p>

      {/* Tab bar */}
      <div className={styles.tabList}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabBtnActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab !== 'per_cup' ? (
        <>
          <div className={styles.paddleStack} onClick={totalPages > 1 ? handleCycle : undefined} style={totalPages > 1 ? { cursor: 'pointer' } : undefined}>
            {ghost2Items && (
              <div className={`${styles.paddleLayer} ${styles.paddleLayerGhost2}`}>
                <Paddle items={ghost2Items} />
              </div>
            )}
            {ghost1Items && (
              <div className={`${styles.paddleLayer} ${styles.paddleLayerGhost1}`}>
                <Paddle items={ghost1Items} />
              </div>
            )}
            <div className={`${styles.paddleLayer} ${styles.paddleLayerFront}`}>
              <Paddle items={currentItems} />
            </div>
          </div>
          {totalPages > 1 && (
            <div className={styles.pageDots}>
              {Array.from({ length: totalPages }).map((_, i) => (
                <div
                  key={i}
                  className={`${styles.pageDot} ${i === paddlePage ? styles.pageDotActive : ''}`}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <PerCupLeaders leaderboard={leaderboard} />
      )}
    </div>
  );
};

export default Leaderboard;
